import {complement, isNil, pickBy} from 'ramda';

import {metadaApi} from '@dms/api/base';

import {
  BulkDeleteDeliveryNoteRequest,
  BulkDeleteDeliveryNoteRequestSchema,
  BulkDeleteDeliveryNoteResponse,
  BulkDeleteDeliveryNoteResponseSchema,
  BulkEditInvoiceDeliveryNoteRequest,
  BulkEditInvoiceDeliveryNoteRequestSchema,
  BulkEditInvoiceDeliveryNoteResponse,
  BulkEditInvoiceDeliveryNoteResponseSchema,
  BulkPatchDeliveryNoteRequest,
  BulkPatchDeliveryNoteRequestSchema,
  BulkPatchDeliveryNoteResponse,
  BulkPatchDeliveryNoteResponseSchema,
  BulkPostCatalogNumberSuggestionsResponse,
  BulkPostCatalogNumberSuggestionsRequest,
  BulkPostReceiveNoteRequest,
  BulkPostReceiveNoteRequestSchema,
  BulkPostReceiveNoteResponse,
  BulkPostReceiveNoteResponseSchema,
  GetDeliveryNoteDuplicatesRequest,
  GetDeliveryNoteDuplicatesRequestSchema,
  GetDeliveryNoteDuplicatesResponse,
  GetDeliveryNoteDuplicatesResponseSchema,
  GetDeliveryNoteManualDownloadRequest,
  GetDeliveryNoteManualDownloadRequestSchema,
  GetDeliveryNoteManualDownloadResponse,
  GetDeliveryNoteManualDownloadResponseSchema,
  GetDeliveryNoteRequest,
  GetDeliveryNoteRequestSchema,
  GetDeliveryNoteResponse,
  GetDeliveryNoteResponseSchema,
  PatchDeliveryNoteRequest,
  PatchDeliveryNoteRequestSchema,
  PatchDeliveryNoteResponse,
  PatchDeliveryNoteResponseSchema,
  PatchDeliveryNoteForCompletedRequest,
  PatchDeliveryNoteForCompletedRequestSchema,
  PatchDeliveryNoteForCompletedResponse,
  PatchDeliveryNoteForCompletedResponseSchema,
  PostDeliveryNoteRequest,
  PostDeliveryNoteRequestSchema,
  PostDeliveryNoteResponse,
  PostDeliveryNoteResponseSchema,
  BulkPostCatalogNumberSuggestionsRequestSchema,
  BulkPostCatalogNumberSuggestionsResponseSchema,
} from './types';

export const metadaWarehouseDeliveryNoteApi = metadaApi.injectEndpoints({
  endpoints: (build) => ({
    getDeliveryNoteDuplicates: build.query<
      GetDeliveryNoteDuplicatesResponse,
      GetDeliveryNoteDuplicatesRequest
    >({
      query: (queryArg) => ({
        url: 'warehouse/v2/delivery-note',
        method: 'GET',
        // Remove nullish query params
        params: pickBy(complement(isNil), {
          supplierId: queryArg.supplierId,
          invoiceNumber: queryArg.invoiceNumber,
          deliveryNoteNumber: queryArg.deliveryNoteNumber,
        }),
        queryArg,
      }),
      extraOptions: {
        requestSchema: GetDeliveryNoteDuplicatesRequestSchema,
        responseSchema: GetDeliveryNoteDuplicatesResponseSchema,
      },
    }),
    getDeliveryNote: build.query<GetDeliveryNoteResponse, GetDeliveryNoteRequest>({
      query: (queryArg) => ({
        url: `warehouse/v4/delivery-note/${queryArg.deliveryNoteId}`,
        method: 'GET',
      }),
      providesTags: (result, error, queryArg) => [
        {type: 'deliveryNote', id: queryArg.deliveryNoteId},
      ],
      extraOptions: {
        requestSchema: GetDeliveryNoteRequestSchema,
        responseSchema: GetDeliveryNoteResponseSchema,
      },
    }),
    postDeliveryNote: build.mutation<PostDeliveryNoteResponse, PostDeliveryNoteRequest>({
      query: (queryArg) => ({
        url: 'warehouse/v3/delivery-note',
        method: 'POST',
        body: queryArg,
      }),
      invalidatesTags: ['deliveryNote'],
      extraOptions: {
        requestSchema: PostDeliveryNoteRequestSchema,
        responseSchema: PostDeliveryNoteResponseSchema,
      },
    }),
    patchDeliveryNote: build.mutation<PatchDeliveryNoteResponse, PatchDeliveryNoteRequest>({
      query: (queryArg) => ({
        url: `warehouse/v1/delivery-note/${queryArg.deliveryNoteId}/manual`,
        method: 'PATCH',
        body: queryArg.body,
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'deliveryNote', id: queryArg.deliveryNoteId},
      ],
      extraOptions: {
        requestSchema: PatchDeliveryNoteRequestSchema,
        responseSchema: PatchDeliveryNoteResponseSchema,
      },
    }),
    patchDeliveryNoteForCompleted: build.mutation<
      PatchDeliveryNoteForCompletedResponse,
      PatchDeliveryNoteForCompletedRequest
    >({
      query: (queryArg) => ({
        url: `warehouse/v1/delivery-note/${queryArg.deliveryNoteId}/completed`,
        method: 'PATCH',
        body: queryArg.body,
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'deliveryNote', id: queryArg.deliveryNoteId},
      ],
      extraOptions: {
        requestSchema: PatchDeliveryNoteForCompletedRequestSchema,
        responseSchema: PatchDeliveryNoteForCompletedResponseSchema,
      },
    }),
    getDeliveryNoteManualDownload: build.query<
      GetDeliveryNoteManualDownloadResponse,
      GetDeliveryNoteManualDownloadRequest
    >({
      query: (queryArg) => ({
        url: 'warehouse/v1/delivery-note/manual-download',
        params: queryArg.params,
      }),
      extraOptions: {
        requestSchema: GetDeliveryNoteManualDownloadRequestSchema,
        responseSchema: GetDeliveryNoteManualDownloadResponseSchema,
      },
    }),
    bulkDeleteDeliveryNote: build.mutation<
      BulkDeleteDeliveryNoteResponse,
      BulkDeleteDeliveryNoteRequest
    >({
      query: (queryArg) => ({
        url: 'warehouse/v1/delivery-note/bulk-delete',
        method: 'PATCH',
        body: queryArg,
      }),
      extraOptions: {
        requestSchema: BulkDeleteDeliveryNoteRequestSchema,
        responseSchema: BulkDeleteDeliveryNoteResponseSchema,
      },
    }),
    bulkPostReceiveNote: build.mutation<BulkPostReceiveNoteResponse, BulkPostReceiveNoteRequest>({
      query: (queryArg) => ({
        url: 'warehouse/v1/delivery-note/bulk-create-receive-note',
        method: 'POST',
        body: queryArg,
      }),
      extraOptions: {
        requestSchema: BulkPostReceiveNoteRequestSchema,
        responseSchema: BulkPostReceiveNoteResponseSchema,
      },
    }),
    bulkPatchDeliveryNote: build.mutation<
      BulkPatchDeliveryNoteResponse,
      BulkPatchDeliveryNoteRequest
    >({
      query: (queryArg) => ({
        url: 'warehouse/v1/delivery-note/bulk-edit',
        method: 'PATCH',
        body: queryArg,
      }),
      invalidatesTags: ['deliveryNote'],
      extraOptions: {
        requestSchema: BulkPatchDeliveryNoteRequestSchema,
        responseSchema: BulkPatchDeliveryNoteResponseSchema,
      },
    }),
    bulkPostCatalogNumberSuggestions: build.mutation<
      BulkPostCatalogNumberSuggestionsResponse,
      BulkPostCatalogNumberSuggestionsRequest
    >({
      query: (queryArg) => ({
        url: 'warehouse/v1/spare-parts/advanced-search',
        method: 'POST',
        body: queryArg,
      }),
      extraOptions: {
        requestSchema: BulkPostCatalogNumberSuggestionsRequestSchema,
        responseSchema: BulkPostCatalogNumberSuggestionsResponseSchema,
      },
    }),
    bulkEditInvoiceDeliveryNote: build.mutation<
      BulkEditInvoiceDeliveryNoteResponse,
      BulkEditInvoiceDeliveryNoteRequest
    >({
      query: (queryArg) => ({
        url: 'warehouse/v1/delivery-note/bulk-edit-invoice',
        method: 'PATCH',
        body: queryArg,
      }),
      invalidatesTags: ['deliveryNote'],
      extraOptions: {
        requestSchema: BulkEditInvoiceDeliveryNoteRequestSchema,
        responseSchema: BulkEditInvoiceDeliveryNoteResponseSchema,
      },
    }),
  }),
});

export const {
  useGetDeliveryNoteDuplicatesQuery,
  useLazyGetDeliveryNoteDuplicatesQuery,
  useGetDeliveryNoteQuery,
  useLazyGetDeliveryNoteQuery,
  usePostDeliveryNoteMutation,
  usePatchDeliveryNoteMutation,
  usePatchDeliveryNoteForCompletedMutation,
  useGetDeliveryNoteManualDownloadQuery,
  useLazyGetDeliveryNoteManualDownloadQuery,
  useBulkDeleteDeliveryNoteMutation,
  useBulkPostReceiveNoteMutation,
  useBulkPatchDeliveryNoteMutation,
  useBulkPostCatalogNumberSuggestionsMutation,
  useBulkEditInvoiceDeliveryNoteMutation,
} = metadaWarehouseDeliveryNoteApi;
